import { useEffect, useState, useCallback } from 'react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Tag, Button, Input, Select, DatePicker, Space, Typography, Tooltip, message } from 'antd';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { queryForeignSyncTasks, type ForeignSyncJobItem } from '@/apis/simulation';

const { Text } = Typography;

export function ForeignSyncTaskListCard(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ForeignSyncJobItem[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState<{
    apiCode: string;
    creator: string;
    status: string | undefined;
    range: [Dayjs | null, Dayjs | null] | null;
  }>({ apiCode: '', creator: '', status: undefined, range: null });

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    try {
      const r = await queryForeignSyncTasks({
        current: page,
        pageSize,
        paginateByJob: true,
        apiCode: filters.apiCode || undefined,
        creator: filters.creator || undefined,
        status: filters.status || undefined,
        submittedAfter: filters.range?.[0]?.toISOString(),
        submittedBefore: filters.range?.[1]?.toISOString(),
      });
      if (r) {
        setItems(r.items as ForeignSyncJobItem[]);
        setPagination({ current: page, pageSize, total: r.total });
      }
    } catch (e) {
      message.error(`加载失败: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: ColumnsType<ForeignSyncJobItem> = [
    {
      title: '同步 job',
      dataIndex: 'dispatchJobId',
      width: 200,
      ellipsis: true,
      render: (v: string) => v ? (
        <Link to={`/scene-generation/foreign-sync-tasks/${v}`} title={v}>
          <Text copyable={{ text: v }}>{v.slice(-16)}</Text>
        </Link>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: '同步状态',
      dataIndex: 'syncStatus',
      width: 100,
      render: (v: string | null | undefined) => {
        if (v === 'SUBMITTED') return <Tag color="processing">同步中</Tag>;
        if (v === 'COMPLETED') return <Tag color="success">同步完成</Tag>;
        if (v === 'SYNC_FAILED') return <Tag color="error">同步失败</Tag>;
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: '场景状态',
      key: 'sceneStatus',
      width: 220,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.sceneQcRunning ? <Tag color="processing">质检中 {r.sceneQcRunning}</Tag> : null}
          {r.scenePassed ? <Tag color="success">合格 {r.scenePassed}</Tag> : null}
          {r.sceneFailed ? <Tag color="error">失败 {r.sceneFailed}</Tag> : null}
          {r.sceneRecorded ? <Tag>已录入 {r.sceneRecorded}</Tag> : null}
        </Space>
      ),
    },
    { title: '总任务数', dataIndex: 'totalCases', width: 100, render: (v: number) => <Tag>{v}</Tag> },
    { title: '成功', dataIndex: 'completedCases', width: 80, render: (v: number) => (v ? <Tag color="success">{v}</Tag> : <Text type="secondary">0</Text>) },
    { title: '失败', dataIndex: 'failedCases', width: 80, render: (v: number) => (v ? <Tag color="error">{v}</Tag> : <Text type="secondary">0</Text>) },
    { title: '同步中', dataIndex: 'submittedCases', width: 80, render: (v: number) => (v ? <Tag color="processing">{v}</Tag> : <Text type="secondary">0</Text>) },
    { title: '创建人', dataIndex: 'creator', width: 120, ellipsis: true, render: (v: string | null | undefined) => v || '—' },
    { title: '提交时间', dataIndex: 'submittedAt', width: 160, render: (v: string | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—') },
    { title: '回调时间', dataIndex: 'callbackAt', width: 160, render: (v: string | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—') },
  ];

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <span>海外数据同步任务</span>
          <Tooltip title="UAE case 建场景后, sim 先起海外 sync job 把 record 从 Azure 拉到国内 BOS, 完成后才会触发场景生成。这里看 sync 阶段进度。">
            <InfoCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      }
      extra={
        <Tooltip title="刷新">
          <Button
            type="text"
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => fetchData(pagination.current, pagination.pageSize)}
          />
        </Tooltip>
      }
    >
      <div style={{
        marginBottom: 12,
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
      }}>
        <Input
          placeholder="apiCode(模糊)"
          value={filters.apiCode}
          allowClear
          style={{ width: 180 }}
          onChange={e => setFilters(p => ({ ...p, apiCode: e.target.value }))}
          onPressEnter={() => fetchData(1, pagination.pageSize)}
        />
        <Input
          placeholder="创建人(前缀匹配)"
          value={filters.creator}
          allowClear
          style={{ width: 180 }}
          onChange={e => setFilters(p => ({ ...p, creator: e.target.value }))}
          onPressEnter={() => fetchData(1, pagination.pageSize)}
        />
        <Select
          placeholder="同步状态"
          value={filters.status}
          allowClear
          style={{ width: 140 }}
          onChange={v => setFilters(p => ({ ...p, status: v }))}
          options={[
            { label: '同步中', value: 'SUBMITTED' },
            { label: '同步完成', value: 'COMPLETED' },
            { label: '同步失败', value: 'SYNC_FAILED' },
          ]}
        />
        <DatePicker.RangePicker
          showTime
          value={filters.range}
          onChange={v => setFilters(p => ({ ...p, range: v as [Dayjs | null, Dayjs | null] | null }))}
          placeholder={['提交时间起', '提交时间止']}
        />
        <Button type="primary" onClick={() => fetchData(1, pagination.pageSize)}>查询</Button>
      </div>
      <Table
        rowKey={(r) => r.dispatchJobId}
        columns={columns}
        dataSource={items}
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 个 job`,
          onChange: (page, size) => fetchData(page, size ?? pagination.pageSize),
        }}
      />
    </Card>
  );
}
